import axios from "axios";
import { DateTime, State } from "../api/calendarEvent";

// The API's error body (NestJS) carries the human-readable reason; the axios
// Error.message is only "Request failed with status code N".
export const extractApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message) return message;
    if (Array.isArray(message) && message.length) return message.join(", ");
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const resizeCloudinary = (
  url: string | undefined,
  size: 200 | 150 | 96 | 48,
) => {
  if (!url) return "";
  if (url!.includes("image/upload") || url.includes("video/upload")) {
    return url.replace("/upload/", "/upload/c_scale,w_" + size + "/");
  }
  return url.replace("/klydoclock/", "/klydoclock/c_scale,w_" + size + "/");
};

export const constructCloudinaryFrameUrl = (
  baseUrl: string,
  format: string,
  frameSelector: string,
): string => {
  if (!baseUrl) return "";

  let url = baseUrl.replace(/\/ac_none\//, "/");
  url = url.replace(/\.(mp4|webp|gif)$/i, `.${format}`);

  if (url.includes("/upload/")) {
    url = url.replace("/upload/", `/upload/${frameSelector}/`);
  } else if (url.includes("/klydoclock/")) {
    url = url.replace("/klydoclock/", `/klydoclock/${frameSelector}/`);
  }

  return url;
};

const TIME_FOR_ONLINE_CHECK = 1800000;

export const statesList = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

export const valueLabelState = (val: string): string => {
  const st = statesList.find((s) => s.value === val);
  if (st) return st.label;
  else return "not found";
};
export const labelValueState = (lbl: string): string => {
  const st = statesList.find((s) => s.label === lbl);
  if (st) return st.value;
  else return "not found";
};
export const percentToPresent = (num: number): string => {
  return (
    (num >= 10
      ? Math.round(num)
      : num > 2
        ? Math.round(num * 10) / 10
        : Math.round(num * 100) / 100) + "%"
  );
};
export enum WeekEnum {
  SUNDAY,
  MONDAY,
  TUESDAY,
  WENDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
}

export enum DeviceCommand {
  DELETE = "DELETE",
  PREVIEW = "PREVIEW",
  UPDATE = "UPDATE",
  UPDATE_REMOTE = "UPDATE_REMOTE",
  ONBOARDING = "ONBOARDING",
  SYNC = "SYNC",
  SYNC_LOGS = "SYNC_LOGS",
  UPDATE_LOG_CONFIG = "UPDATE_LOG_CONFIG",
  LOGCAT = "LOGCAT",
  BLUETOOTH = "BLUETOOTH",
  GET_GIFT = "GET_GIFT",
  SEND_ROOM_DB = "SEND_ROOM_DB",
  STREAM_SQL_QUERIES = "STREAM_SQL_QUERIES",
  SYNC_ALL_SETTINGS_TO_FIRESTORE = "SYNC_ALL_SETTINGS_TO_FIRESTORE",
  RUN_MIGRATIONS = "RUN_MIGRATIONS",
  SYNC_ANALYTICS = "SYNC_ANALYTICS",
  STREAM_ADB_SHELL = "STREAM_ADB_SHELL",
  PLAY_SOUND = "PLAY_SOUND",
  SYNC_TIME = "SYNC_TIME",
  SEND_CRASH_BUNDLE = "SEND_CRASH_BUNDLE",
  RUN_MONITORS = "RUN_MONITORS",
  SYNC_MONITOR_REPORTS = "SYNC_MONITOR_REPORTS",
  FETCH_RATING_FEED = "FETCH_RATING_FEED",
  RESET_RATINGS = "RESET_RATINGS",
  TRIGGER_ECO_MODE = "TRIGGER_ECO_MODE",
  REFRESH_CONFIG = "REFRESH_CONFIG",
}
export interface DeviceCommandInfo {
  command: string;
  headline: string;
  disableForMultiple?: boolean;
  hideForMultiple?: boolean;
  minVersion?: number;
  maxVersion?: number;
}

export const MODAL_ACTIONS_INFO: Record<DeviceCommand, DeviceCommandInfo> = {
  [DeviceCommand.DELETE]: {
    command: "deleteKlydos",
    headline: "Delete Klydos",
  },
  [DeviceCommand.PREVIEW]: {
    command: "playKlydo",
    headline: "Play Klydo",
    minVersion: 501,
    maxVersion: 608,
    hideForMultiple: true,
  },
  [DeviceCommand.UPDATE]: {
    command: "updateApp",
    headline: "Update App",
  },
  [DeviceCommand.UPDATE_REMOTE]: {
    command: "updateRemote",
    headline: "Update Remote",
  },
  [DeviceCommand.ONBOARDING]: {
    command: "onBoarding",
    headline: "Onboard clock",
  },
  [DeviceCommand.SYNC]: {
    command: "syncPool",
    headline: "Sync Pool",
  },
  [DeviceCommand.SYNC_LOGS]: {
    command: "syncLogs",
    headline: "Sync Logs",
  },
  [DeviceCommand.UPDATE_LOG_CONFIG]: {
    command: "updateLogConfig",
    headline: "Update Log Config",
  },
  [DeviceCommand.LOGCAT]: {
    command: "logcat",
    headline: "Logcat",
    disableForMultiple: true,
  },
  [DeviceCommand.BLUETOOTH]: {
    command: "bluetooth",
    headline: "Reset bluetooth",
  },
  [DeviceCommand.GET_GIFT]: {
    command: "getGift",
    headline: "Get Gift",
    minVersion: 481.85,
  },
  [DeviceCommand.SEND_ROOM_DB]: {
    command: "sendRoomDB",
    headline: "Get Room DB",
    disableForMultiple: true,
    minVersion: 595,
  },
  [DeviceCommand.STREAM_SQL_QUERIES]: {
    command: "streamSQLQueries",
    headline: "Stream Sql Queries",
    disableForMultiple: true,
    minVersion: 595,
  },
  [DeviceCommand.SYNC_ALL_SETTINGS_TO_FIRESTORE]: {
    command: "syncAllSettingsToFirestore",
    headline: "Sync all settings",
    minVersion: 600,
  },
  [DeviceCommand.RUN_MIGRATIONS]: {
    command: "runMigrations",
    headline: "Run Migrations",
    minVersion: 600.4,
  },
  [DeviceCommand.SYNC_ANALYTICS]: {
    command: "syncAnalytics",
    headline: "Sync Analytics",
  },
  [DeviceCommand.STREAM_ADB_SHELL]: {
    command: "streamAdbShell",
    headline: "ADB Shell",
    disableForMultiple: true,
    minVersion: 610,
  },
  [DeviceCommand.PLAY_SOUND]: {
    command: "playSound",
    headline: "Play Sound",
    minVersion: 615.7,
  },
  [DeviceCommand.SYNC_TIME]: {
    command: "syncTime",
    headline: "Sync Time",
    // TODO: set minVersion to the release that ships SyncTimeCommandExecutor
    // (currently on branch fix/sync-time-command off release/615.1).
    // Without this, the button shows for all devices and is a no-op on
    // versions that don't have the executor.
    // minVersion: 615.2,
  },
  [DeviceCommand.SEND_CRASH_BUNDLE]: {
    command: "sendCrashBundle",
    headline: "Get Crash Bundle",
    // TODO: set minVersion to the release that ships SendCrashBundleCommandExecutor.
    // Until then the button shows for all devices and is a no-op on versions
    // without the executor.
    // minVersion: <release>,
  },
  [DeviceCommand.RUN_MONITORS]: {
    command: "runMonitors",
    headline: "Run Monitors",
    // params: array of clock-internal monitor names to run; an empty array
    // runs all monitors on the device.
    // TODO: set minVersion to the release that ships RunMonitorsCommandExecutor.
    // Until then the button shows for all devices and is a no-op on versions
    // without the executor.
    // minVersion: <release>,
  },
  [DeviceCommand.SYNC_MONITOR_REPORTS]: {
    command: "syncMonitorReports",
    headline: "Sync Monitor Reports",
    // No params — the clock pushes its pending monitor failure reports.
    // TODO: set minVersion to the release that ships
    // SyncMonitorReportsCommandExecutor. Until then the button shows for all
    // devices and is a no-op on versions without the executor.
    // minVersion: <release>,
  },
  [DeviceCommand.FETCH_RATING_FEED]: {
    command: "fetchRatingFeed",
    headline: "Fetch Rating Feed",
    // Forces the (artist) clock to refresh its reviews feed from
    // clock/klydos/rating/to-review. No-op on non-artist clocks.
  },
  [DeviceCommand.RESET_RATINGS]: {
    command: "resetRatings",
    headline: "Reset Ratings",
    // Deletes all ratings this device made (server + local) and re-fetches, so
    // previously-rated klydos become reviewable again.
  },
  [DeviceCommand.REFRESH_CONFIG]: {
    command: "refreshConfig",
    headline: "Refresh Config",
    // Re-fetches clock/config now instead of waiting for the daily 04:xx fetch.
    // Use it after saving a global value or a per-clock override that has to land
    // immediately.
    // TODO: set minVersion to the release that ships RefreshConfigCommandExecutor.
    // Until then the button shows for all devices and is a no-op on versions
    // without the executor.
    // minVersion: <release>,
  },
  [DeviceCommand.TRIGGER_ECO_MODE]: {
    command: "triggerEcoMode",
    headline: "Trigger ECO Mode",
    // Forces the device into ECO mode immediately, bypassing the 7-day idle wait.
    // No-op if ECO is switched OFF. Persists until the next user action on the device.
    // TODO: set minVersion to the release that ships TriggerEcoModeCommandExecutor.
    // Until then the button shows for all devices and is a no-op on versions without it.
    // minVersion: <release>,
  },
};

export const socialMediaList = [
  "instagram",
  "linktree",
  "twitter",
  "facebook",
  "tiktok",
  "behance",
  "dribbble",
  "youtube",
  "patreon",
  "tumblr",
  "deviantart",
  "artstation",
  "wikipedia",
  "imdb",
  "vimeo",
  "pinterest",
  "opensea",
  "rarible",
  "superrare",
  "foundation",
  "nifty",
];
type ViewType = "video" | "image" | "none";

const imgTypes = [
  "jpg",
  "gif",
  "avif",
  "apng",
  "png",
  "ico",
  "cur",
  "jpeg",
  "jfif",
  "pjpeg",
  "pjp",
  "tif",
  "webp",
  "bmp",
];
const videoTypes = ["mp4", "mpge", "avi", "mov", "mkv", "webm", "ogg", "ogv"];

export const isVideo = (url: string): ViewType => {
  if (!url) return "none";
  const arr = url.split(".");
  const typ = arr[arr.length - 1];
  return imgTypes.includes(typ)
    ? "image"
    : videoTypes.includes(typ)
      ? "video"
      : "none";
};

/**
 * Reads the shapes a timestamp arrives in from the API: epoch millis, an ISO
 * string, a Date, or a serialized Firestore Timestamp (`{ seconds }` /
 * `{ _seconds }`, with or without a `toDate` method). Returns undefined when
 * the value is missing or cannot be read as a date.
 */
export const toDateSafe = (value: unknown): Date | undefined => {
  if (value === null || value === undefined) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value === "number" || typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  if (typeof value === "object") {
    const timestamp = value as {
      seconds?: number;
      _seconds?: number;
      toDate?: () => Date;
    };
    if (typeof timestamp.toDate === "function") {
      const date = timestamp.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime())
        ? date
        : undefined;
    }
    const seconds = timestamp.seconds ?? timestamp._seconds;
    if (typeof seconds === "number") return new Date(seconds * 1000);
  }
  return undefined;
};

export const onlineCheck = (t: Date | undefined) => {
  if (t === undefined) return false;
  return Math.abs(new Date().getTime() - t.getTime()) < TIME_FOR_ONLINE_CHECK;
};

/**
 * Escapes one CSV field: doubles inner quotes and wraps the value whenever it
 * contains a delimiter, quote or newline. A leading `=`, `+`, `-` or `@` is
 * prefixed with a tab so spreadsheets do not read the value as a formula.
 */
const escapeCsvField = (value: string | number): string => {
  const raw = String(value ?? "");
  const safe = /^[=+\-@]/.test(raw) ? `\t${raw}` : raw;
  return /["\n\r,]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/**
 * Builds a CSV from `headers` + `rows` and triggers a browser download. The
 * content is prefixed with a BOM so Excel opens non-ASCII names correctly.
 */
export const downloadCsv = ({
  filename,
  headers,
  rows,
}: {
  filename: string;
  headers: string[];
  rows: (string | number)[][];
}) => {
  const csv = [headers, ...rows]
    .map((row) => row.map(escapeCsvField).join(","))
    .join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const download = (url: string, name: string) => {
  if (!url) {
    throw new Error("Resource URL not provided! You need to provide one");
  }
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const blobURL = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobURL;
      a.style.display = "display: none";
      a.download = name;
      document.body.appendChild(a);
      a.click();
    })
    .catch(console.log);
};

export function secondsToTime(secondsNum: number) {
  const months = Math.floor(secondsNum / 2629743);
  const days = Math.floor((secondsNum % 2629743) / 86400);
  const hours = Math.floor((secondsNum % 86400) / 3600);
  const minutes = Math.floor((secondsNum - hours * 3600 - days * 86400) / 60);
  const seconds = secondsNum % 60;

  const monthsString = months < 10 ? "0" + months : months.toString();
  const daysString = days < 10 ? "0" + days : days.toString();
  const hoursString = hours < 10 ? "0" + hours : hours.toString();
  const minutesString = minutes < 10 ? "0" + minutes : minutes.toString();
  const secondsString = seconds < 10 ? "0" + seconds : seconds.toString();

  return (
    monthsString +
    ":" +
    daysString +
    ":" +
    hoursString +
    ":" +
    minutesString +
    ":" +
    secondsString
  );
}

export type ParseUrlInput = {
  url: string;
  isStatic?: boolean;
  size?: number;
};

export const parseCloudinaryUrl = (input: ParseUrlInput) => {
  let { url } = input;
  if (!url) return;
  const { isStatic, size } = input;
  const type = url.split(".").pop();
  const params = `c_scale,w_${size}`;
  const framesParams = "so_auto";
  if (isStatic) {
    return constructCloudinaryFrameUrl(url, "jpg", framesParams);
  }
  if (type === "mp4") {
    if (size) {
      if (url!.includes("image/upload") || url.includes("video/upload")) {
        url = url.replace("/upload/", `/upload/${params}/`);
      } else {
        url = url.replace("/klydoclock/", `/klydoclock/${params}/`);
      }
    }
  }
  return url;
};

export function getMediaType(url: string): ViewType {
  if (url.startsWith("data"))
    return url.startsWith("data:video")
      ? "video"
      : url.startsWith("data:image")
        ? "image"
        : "none";
  const arr = url.split(".");
  const typ = arr[arr.length - 1];
  return imgTypes.includes(typ)
    ? "image"
    : videoTypes.includes(typ)
      ? "video"
      : "none";
}

export const formatDateTime = (dateTime: DateTime): string => {
  const jsDate = new Date(
    dateTime.year ?? new Date().getFullYear(),
    dateTime.month - 1,
    dateTime.day,
  );
  return jsDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const dateToDateTime = (date: Date): DateTime => {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

export const areStatesEqual = (
  state1: State | null | undefined,
  state2: State | null | undefined,
): boolean => {
  if (!state1 && !state2) return true;

  if (!state1 || !state2) return false;

  return (
    (state1?.iso || "") === (state2?.iso || "") &&
    (state1?.abbrev || "") === (state2?.abbrev || "") &&
    (state1?.name || "") === (state2?.name || "")
  );
};
